import React from 'react';
import { ArrowUpRight, BrainCircuit, Code2, FlaskConical } from 'lucide-react';
import SEO from '@/components/seo/SEO';
import { Breadcrumbs } from '@/components/seo/Breadcrumbs';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

const creatorUrl = 'https://danielgaskins.com/';

const personSchema = {
  '@context': 'https://schema.org',
  '@type': 'Person',
  '@id': `${creatorUrl}#daniel-gaskins`,
  name: 'Daniel Gaskins',
  url: creatorUrl,
  jobTitle: 'Applied AI and Machine Learning Engineer',
  description:
    'Daniel Gaskins is the creator of RunOffTrack and a founder and applied AI and machine learning engineer.',
  knowsAbout: [
    'Applied artificial intelligence',
    'Machine learning engineering',
    'Agent evaluation',
    'Computer vision',
    'Production software systems',
  ],
};

const pageSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  '@id': 'https://runofftrack.com/about/daniel-gaskins#page',
  url: 'https://runofftrack.com/about/daniel-gaskins',
  name: 'Daniel Gaskins, Creator of RunOffTrack',
  description:
    'Meet Daniel Gaskins, the creator of RunOffTrack and an applied AI and machine learning engineer.',
  mainEntity: { '@id': `${creatorUrl}#daniel-gaskins` },
  isPartOf: {
    '@type': 'WebSite',
    name: 'RunOffTrack',
    url: 'https://runofftrack.com/',
  },
};

const DanielGaskinsPage: React.FC = () => (
  <div className="min-h-screen bg-background font-sans selection:bg-primary/20">
    <SEO
      title="Daniel Gaskins, Creator of RunOffTrack"
      description="Meet Daniel Gaskins, the creator of RunOffTrack and an applied AI and machine learning engineer who builds reliable real-world software systems."
      canonical="/about/daniel-gaskins"
      jsonLd={[personSchema, pageSchema]}
    />

    <section className="border-b border-border bg-muted/20 py-16 md:py-24">
      <div className="container mx-auto max-w-5xl px-4">
        <Breadcrumbs items={[{ label: 'Daniel Gaskins', href: '/about/daniel-gaskins' }]} />
        <div className="max-w-3xl">
          <p className="mb-4 text-sm font-semibold uppercase tracking-[0.18em] text-primary">
            Creator of RunOffTrack
          </p>
          <h1 className="mb-6 text-4xl font-bold tracking-tight text-foreground md:text-6xl">
            Daniel Gaskins
          </h1>
          <p className="text-xl leading-relaxed text-muted-foreground md:text-2xl">
            Daniel Gaskins created RunOffTrack. He is a founder and applied AI and machine learning engineer who turns difficult, ambiguous problems into software that can be tested and used in the real world.
          </p>
        </div>
      </div>
    </section>

    <section className="py-16 md:py-24">
      <div className="container mx-auto grid max-w-5xl gap-12 px-4 lg:grid-cols-[1.5fr_1fr]">
        <article className="prose prose-lg max-w-none dark:prose-invert">
          <h2>About the creator</h2>
          <p>
            Daniel designed and developed RunOffTrack as a practical system for making complex stormwater-compliance information and field resources easier to navigate. The project combines product design, structured content, interactive tools, and production web engineering.
          </p>
          <p>
            His broader work spans agent evaluation, document AI, computer vision, cross-runtime machine learning, and the dependable application systems that surround model output. He studied condensed matter physics at the University of California, San Diego before moving into founder-led software and machine-learning work.
          </p>
          <p>
            Visit{' '}
            <a href={creatorUrl} className="font-semibold">
              DanielGaskins.com
            </a>{' '}
            to explore Daniel Gaskins&apos;s selected projects, technical writing, open-source work, and professional background.
          </p>

          <Button asChild size="lg" className="not-prose mt-4">
            <a href={creatorUrl} aria-label="Visit Daniel Gaskins's portfolio">
              View Daniel Gaskins&apos;s portfolio
              <ArrowUpRight className="ml-2 h-4 w-4" />
            </a>
          </Button>
        </article>

        <aside className="space-y-4" aria-label="Daniel Gaskins areas of work">
          <Card>
            <CardContent className="flex gap-4 p-6">
              <BrainCircuit className="mt-1 h-6 w-6 shrink-0 text-primary" />
              <div>
                <h2 className="font-semibold text-foreground">Applied AI and ML</h2>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                  Building and evaluating useful systems around models, tools, and real workflows.
                </p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex gap-4 p-6">
              <FlaskConical className="mt-1 h-6 w-6 shrink-0 text-primary" />
              <div>
                <h2 className="font-semibold text-foreground">Experimentation</h2>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                  Turning uncertain ideas into testable questions and inspecting where systems fail.
                </p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex gap-4 p-6">
              <Code2 className="mt-1 h-6 w-6 shrink-0 text-primary" />
              <div>
                <h2 className="font-semibold text-foreground">Production software</h2>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                  Shipping maintainable products, automation, and open-source engineering work.
                </p>
              </div>
            </CardContent>
          </Card>
        </aside>
      </div>
    </section>
  </div>
);

export default DanielGaskinsPage;
